import type { KitFontId, ThemeTypeStyle } from '../releaseThemes';

import { fitStoryLine, storyLayout, type StoryBox, type StoryItem, coverFrameHeight } from './storyLayout';
import { STORY_FORMATS, VIDEO_STORY_UNIT, type StoryFormat, type StoryFormatId } from './storyFormats';
import { storyFrameOf } from './storyFrames';
import { storyAlpha, type StoryColours, type StoryCss } from './storyStyles';
import { clamp01, nearestInRange, nudge, outCubic, progress, round3, type VideoBeatGrid } from './videoMotion';
import { textWidthEm } from './kitFontMetrics';

/**
 * The artwork in motion (design `asset-redesign/q Artwork in motion - new`, owner answers
 * 2026-10-08): the full square cover in its theme frame on a closed drift path that pulses on the
 * kick, 16 spectrum bars in the theme shape under it on every style, one id line ("Swamps ·
 * LaziiBee") the whole time, and the CTA pill and the SmartLink from the downbeat near 11 s. The
 * pill leaves in the last 0.4 s and the drift path is closed, so the last frame equals frame 0 and
 * the video loops. On Free the watermark stays at the foot of the column the whole time. One length
 * (15 s); the pill comes from the date and the pre-save state (no moment).
 */

/** The shapes of the artwork in motion: the 9:16 short and the 4:5 and square feed copies. */
export type ArtworkMotionShape = '9:16' | '4:5' | '1:1';

/**
 * The cover motion of the style (design q): Pulse grows 6 % on the kick, every other style 2.5 %.
 * Liquid adds the ink over the cover; Particles draw their own layer in place of the bars.
 */
export type ArtworkMotionKind = 'drift' | 'pulse' | 'liquid' | 'particles';

export interface ArtworkMotionTiming {
  /** The CTA group comes in on this downbeat (Swamps: 11.04 s). */
  ctaInSec: number;
  /** The pill nudges on these downbeats. */
  nudgeSecs: number[];
  /** The CTA group fades out from here to the end (the length less 0.4 s). */
  leaveSec: number;
  /** The length of the loop: the drift path has this period. */
  lengthSec: number;
  /** One bar of the window: the bars rise from rest over the first half bar and rest again over the last bar. Absent: 2 s. */
  barSec?: number;
}

/** The texts and the timing of one artwork in motion (`plan.short`, kind `artworkMotion`). */
export interface KitArtworkMotionVideo {
  kind: 'artworkMotion';
  shape: ArtworkMotionShape;
  title: string;
  artist: string;
  /** The pill by the date and the pre-save state ("Pre-save now", "Listen now", "Coming soon"). */
  cta: string;
  /** The SmartLink address with no scheme; null: none (the pill still shows). */
  link: string | null;
  motion: ArtworkMotionKind;
  timing: ArtworkMotionTiming;
  /** The poster: frame 0, which is also the loop point. */
  posterSec: number;
}

/** The one length of the artwork in motion (owner answer 5 of page q: no 30 s version). */
export const ARTWORK_MOTION_LENGTH_SEC = 15;
/** The bar count of the spectrum under the cover. */
export const ARTWORK_MOTION_BAR_COUNT = 16;
/** The CTA group stays at least this long before the loop point. */
const CTA_MIN_SEC = 3.5;

/** The Story format of the CTA group of a shape: its canvas, its safe column and its sizes. */
export function artworkMotionFormatId(shape: ArtworkMotionShape): StoryFormatId {
  if (shape === '1:1') {
    return 'feed11';
  }
  return shape === '4:5' ? 'feed45' : 'video916';
}

/** The sizes of the stack of design q in the 9:16 frame (px of 1080 x 1920). */
const STACK = {
  /** The stack starts 40 px under the top of the safe column. */
  top: 40,
  gap: 34,
  cover: 600,
  barsHeight: 110,
  barsWidth: 600,
  idTitle: 64 * VIDEO_STORY_UNIT,
  idArtist: 40 * VIDEO_STORY_UNIT,
  idGap: 18,
  dot: 10,
};

/** The cover of a theme in the stack: Hype 540 (and 12 px more room above), Retro 500, Handmade 520. */
function coverSizeOf(themeId: string): { size: number; marginTop: number } {
  switch (themeId) {
    case 'hype':
      return { size: 540, marginTop: 12 };
    case 'retro':
      return { size: 500, marginTop: 0 };
    case 'handmade':
      return { size: 520, marginTop: 0 };
    default:
      return { size: STACK.cover, marginTop: 0 };
  }
}

export interface ArtworkMotionLayoutInput {
  video: Pick<KitArtworkMotionVideo, 'shape' | 'title' | 'artist' | 'cta' | 'link'>;
  themeId: string;
  fonts: { display: KitFontId; text: KitFontId };
  typeStyle: { display: ThemeTypeStyle & { scale: number }; text: ThemeTypeStyle };
  /** Free plans: the watermark at the foot of the column for the whole video. */
  watermark?: boolean;
}

export interface ArtworkMotionLayout {
  format: StoryFormat;
  /** The outer box of the cover frame and whether a Retro record shows. */
  cover: { box: StoryBox; withRecord: boolean };
  bars: { box: StoryBox; count: number };
  /**
   * The id line: the title, the accent dot, the artist, centred in `line` (the renderers lay the row
   * out with flex, `gap` apart). The title and the artist boxes are the measured estimates.
   */
  id: {
    line: StoryBox;
    gap: number;
    title: { box: StoryBox; sizePx: number; text: string };
    dot: StoryBox;
    artist: { box: StoryBox; sizePx: number; text: string };
  };
  /** The pill and the SmartLink line (the CTA group, at the bottom of the column, over the watermark). */
  cta: StoryItem[];
  watermark: StoryItem | null;
  /** The scale of the stack when the shape is short (1 on 9:16). */
  scale: number;
}

/**
 * Where the 9:16 stack of design q goes in the file of a shape: the 9:16 file shows it as it is; a
 * 4:5 or square file shows the safe column of the 9:16 frame (x 140 to 940, y 270 to 1440) scaled
 * down to fit the safe column of the feed post frame, centred. `scale` and `x`, `y` map a point of
 * the 1080 x 1920 stack: `x + px * scale`. The ground and the ornaments fill the whole file. Pure.
 */
export function artworkMotionPlacement(shape: ArtworkMotionShape): {
  format: StoryFormat;
  scale: number;
  x: number;
  y: number;
} {
  const format = STORY_FORMATS[artworkMotionFormatId(shape)];
  if (format.id === 'video916') {
    return { format, scale: 1, x: 0, y: 0 };
  }
  const source = STORY_FORMATS.video916;
  const sourceWidth = source.size.width - 2 * (source.marginX ?? 140);
  const sourceHeight = source.safeBottom - source.safeTop;
  const margin = format.marginX ?? 110;
  const width = format.size.width - 2 * margin;
  const height = format.safeBottom - format.safeTop;
  const scale = round3(Math.min(width / sourceWidth, height / sourceHeight));
  return {
    format,
    scale,
    x: round3(format.size.width / 2 - (source.size.width / 2) * scale),
    y: round3(format.safeTop + (height - sourceHeight * scale) / 2 - source.safeTop * scale),
  };
}

/**
 * The layout of the artwork in motion in the 9:16 frame of design q: the stack (cover, bars, id
 * line) from the top of the safe column and the CTA group at its bottom, the same CTA rule as the
 * Story (`storyLayout`); on Free the watermark lockup (design w0) at the foot of the column. Every box is in
 * the 1080 x 1920 canvas; a 4:5 or square file places it with `artworkMotionPlacement`. Pure.
 */
export function artworkMotionLayout(input: ArtworkMotionLayoutInput): ArtworkMotionLayout {
  const { video, themeId, fonts, typeStyle } = input;
  // Every shape is the 9:16 stack of design q; `artworkMotionPlacement` fits it into a 4:5 or square file.
  const formatId: StoryFormatId = 'video916';
  const format = STORY_FORMATS[formatId];
  const frame = storyFrameOf(themeId);
  const ctaLayout = storyLayout({
    story: {
      format: formatId,
      moment: 'outNow',
      title: video.title,
      artist: video.artist,
      kicker: null,
      hero: null,
      nowWord: null,
      dayLabel: null,
      when: null,
      cta: video.cta,
      link: video.link,
      linkNote: null,
      withLogo: false,
    },
    themeId,
    fonts,
    typeStyle,
    watermark: input.watermark === true,
  });
  const cta = ctaLayout.items.filter((item) => item.kind === 'pill' || item.kind === 'link');
  const watermark = ctaLayout.items.find((item) => item.kind === 'watermark') ?? null;
  const ctaTop = Math.min(...cta.map((item) => item.box.y));
  const top = format.safeTop + (format.id === 'video916' ? STACK.top : 0);
  const coverSpec = coverSizeOf(themeId);
  const unit = format.id === 'video916' ? 1 : format.unit;
  const naturalCoverHeight = coverFrameHeight(themeId, coverSpec.size, false, unit);
  const display = { id: fonts.display, style: typeStyle.display };
  const text = { id: fonts.text, style: typeStyle.text };
  const titleSize = STACK.idTitle * frame.factors.display * typeStyle.display.scale;
  const artistSize = STACK.idArtist * frame.factors.text;
  const idHeight = Math.max(titleSize * 0.92, artistSize * 1.1);
  const natural = coverSpec.marginTop + naturalCoverHeight + STACK.gap + STACK.barsHeight + STACK.gap + idHeight;
  const room = ctaTop - STACK.gap - top;
  const scale = round3(Math.min(1, room / natural));
  const { width } = format.size;
  const coverSize = Math.round(coverSpec.size * scale);
  const coverHeight = Math.round(coverFrameHeight(themeId, coverSize, false, unit));
  // Retro: the sleeve moves left by 24 % of its width, so the record ends inside the frame.
  const shift = themeId === 'retro' ? coverSize * 0.24 : 0;
  let y = top + coverSpec.marginTop * scale;
  const cover = {
    box: { x: Math.round((width - coverSize) / 2 - shift), y: Math.round(y), w: coverSize, h: coverHeight },
    withRecord: themeId === 'retro',
  };
  y += coverHeight + STACK.gap * scale;
  const barsWidth = Math.round(STACK.barsWidth * scale);
  const barsHeight = Math.round(STACK.barsHeight * scale);
  const bars = {
    box: { x: Math.round((width - barsWidth) / 2), y: Math.round(y), w: barsWidth, h: barsHeight },
    count: ARTWORK_MOTION_BAR_COUNT,
  };
  y += barsHeight + STACK.gap * scale;
  // The id line: title · artist on one line; it shrinks to the column width.
  const margin = format.marginX ?? frame.marginX;
  const column = { x: margin, w: width - 2 * margin };
  const gap = STACK.idGap * scale;
  const dot = STACK.dot * scale;
  const titleFit = fitStoryLine(video.title, display, titleSize * scale, column.w * 0.62, false);
  const artistFit = fitStoryLine(video.artist, text, artistSize * scale, column.w * 0.38 - 2 * gap - dot, false);
  const titleWidth = Math.min(column.w * 0.62, widthOf(video.title, display, titleFit.sizePx));
  const artistWidth = Math.min(column.w * 0.38, widthOf(video.artist, text, artistFit.sizePx));
  const lineWidth = titleWidth + gap + dot + gap + artistWidth;
  const left = (width - lineWidth) / 2;
  const lineHeight = Math.max(titleFit.sizePx * 0.92, artistFit.sizePx * 1.1);
  const id = {
    line: { x: column.x, y: round3(y), w: column.w, h: round3(lineHeight) },
    gap,
    title: {
      box: { x: round3(left), y: round3(y), w: round3(titleWidth), h: round3(lineHeight) },
      sizePx: titleFit.sizePx,
      text: video.title,
    },
    dot: { x: round3(left + titleWidth + gap), y: round3(y + (lineHeight - dot) / 2), w: dot, h: dot },
    artist: {
      box: {
        x: round3(left + titleWidth + 2 * gap + dot),
        y: round3(y),
        w: round3(artistWidth),
        h: round3(lineHeight),
      },
      sizePx: artistFit.sizePx,
      text: video.artist,
    },
  };
  return { format, cover, bars, id, cta, watermark, scale };
}

function widthOf(textValue: string, font: { id: KitFontId; style: ThemeTypeStyle }, sizePx: number): number {
  return round3(textWidthEm(textValue, font.id, font.style) * sizePx);
}

/**
 * The timing of an artwork in motion from the beat grid of its window: the CTA comes in on the
 * last downbeat that leaves it 3.5 s on screen, the pill nudges on the next downbeats up to 1.5 s
 * before the end, the CTA leaves in the last 0.4 s. Pure.
 */
export function artworkMotionTiming(grid: VideoBeatGrid, lengthSec: number): ArtworkMotionTiming {
  const latest = lengthSec - CTA_MIN_SEC;
  const ctaInSec =
    [...grid.downbeats].reverse().find((beat) => beat <= latest && beat >= lengthSec * 0.55) ??
    nearestInRange(grid.beats, latest, lengthSec * 0.55, latest) ??
    round3(latest);
  const nudgeSecs = grid.downbeats.filter((beat) => beat > ctaInSec + 0.5 && beat <= lengthSec - 1.5);
  const gaps = grid.downbeats
    .slice(1)
    .map((beat, index) => beat - (grid.downbeats[index] ?? beat))
    .filter((gap) => gap > 0.5 && gap < 6)
    .sort((a, b) => a - b);
  const barSec = round3(gaps[Math.floor(gaps.length / 2)] ?? 2);
  return { ctaInSec, nudgeSecs, leaveSec: round3(lengthSec - 0.4), lengthSec, barSec };
}

/** The frame rate of the kit videos (the envelope ends on the last frame). */
const FPS = 30;

/**
 * The song envelope of an artwork in motion at `t` (0..1): it rises from rest over the first half
 * bar and falls back to rest over the last bar, so the bars, the kick and the haze of the last
 * frame are those of frame 0 and the loop has no jump (owner decision 2026-10-08). Pure.
 */
export function artworkMotionEnvelope(t: number, timing: Pick<ArtworkMotionTiming, 'lengthSec' | 'barSec'>): number {
  const bar = timing.barSec ?? 2;
  const last = timing.lengthSec - 1 / FPS;
  const rise = progress(t, 0, bar / 2);
  const fall = progress(t, last - bar, last);
  const smooth = (x: number): number => x * x * (3 - 2 * x);
  return round3(smooth(rise) * (1 - smooth(fall)));
}

export interface ArtworkMotionPose {
  /** The cover: a closed drift path and the kick scale. */
  cover: { x: number; y: number; scale: number };
  cta: { opacity: number; scale: number };
  pill: { scale: number };
  haze: number;
  /** The song envelope (`artworkMotionEnvelope`): the renderers scale the bar and particle levels by it. */
  levels: number;
  /** The strength of the Liquid ink (0 at frame 0 and at the loop point, 1 at the middle). */
  ink: number;
}

/**
 * The pose of an artwork in motion at the time `t` (design q, `VideoFrame` k-motion): the drift
 * scale 1 → 1.04 → 1, x 14 px and y 10 px on one closed path over the length (frame `length` =
 * frame 0), the kick 2.5 % (Pulse 6 %), the CTA from its downbeat (0.36 s, from 92 %) until it
 * fades over the last 0.4 s. Pure.
 */
export function artworkMotionPose(
  t: number,
  video: Pick<KitArtworkMotionVideo, 'motion' | 'timing'>,
  signals: { kick: number; energy: number }
): ArtworkMotionPose {
  const { timing } = video;
  const phase = (2 * Math.PI * t) / timing.lengthSec;
  const amount = video.motion === 'pulse' ? 0.06 : 0.025;
  const ctaIn = outCubic(progress(t, timing.ctaInSec, timing.ctaInSec + 0.36));
  // The CTA is gone on the last frame (the length less one frame), so the last frame equals frame 0.
  const leave = progress(t, timing.leaveSec, timing.lengthSec - 1 / FPS);
  const levels = artworkMotionEnvelope(t, timing);
  return {
    cover: {
      x: round3(14 * Math.sin(phase)),
      y: round3(-10 * Math.sin(2 * phase)),
      scale: round3(1 + (0.04 * (1 - Math.cos(phase))) / 2 + amount * clamp01(signals.kick) * levels),
    },
    cta: { opacity: round3(ctaIn * (1 - leave)), scale: round3(0.92 + 0.08 * ctaIn) },
    pill: { scale: round3(timing.nudgeSecs.reduce((sum, at) => sum + nudge(t, at), 1)) },
    haze: round3(0.35 + 0.65 * clamp01(signals.energy) * levels),
    levels,
    ink: video.motion === 'liquid' ? round3((1 - Math.cos(phase)) / 2) : 0,
  };
}

/** The gap between two bars of a theme (design q `.bars`): Noir 30, Retro 8, every other 12 px. */
export function artworkMotionBarGap(themeId: string): number {
  if (themeId === 'dark') {
    return 30;
  }
  return themeId === 'retro' ? 8 : 12;
}

/**
 * The style of bar `index` at the level `level` (0..1) in the theme shape (design q `.bars i`):
 * Clean thin round, Hype black blocks with an accent shadow, Dreamy glowing dots that float, Noir
 * hairlines with an accent every fourth, Warm arches, Retro striped, Neon tubes, Handmade marker
 * strokes. The bars are centred in a box `height` px high. Pure.
 */
export function artworkMotionBarStyle(
  themeId: string,
  colors: StoryColours,
  index: number,
  level: number,
  height: number
): StoryCss {
  const k = height / STACK.barsHeight;
  const barHeight = Math.round((12 + 98 * clamp01(level)) * k);
  const base: StoryCss = { flex: 'none', display: 'block', height: barHeight, background: colors.accent };
  switch (themeId) {
    case 'clean':
      return { ...base, width: Math.round(14 * k), borderRadius: 7 * k };
    case 'hype':
      return {
        ...base,
        width: Math.round(30 * k),
        borderRadius: 0,
        background: colors.surface,
        boxShadow: `${Math.round(6 * k)}px ${Math.round(6 * k)}px 0 ${colors.accent}`,
      };
    case 'dreamy':
      return {
        ...base,
        width: Math.round(22 * k),
        height: Math.round(22 * k),
        borderRadius: '50%',
        opacity: 0.85,
        filter: 'blur(1px)',
        boxShadow: `0 0 ${Math.round(22 * k)}px ${colors.accent}`,
        transform: `translateY(${Math.round((-44 * clamp01(level) + 22) * k)}px)`,
      };
    case 'dark':
      return {
        ...base,
        width: Math.max(2, Math.round(6 * k)),
        borderRadius: 0,
        background: index % 4 === 0 ? colors.accent : colors.text,
      };
    case 'warm':
      return { ...base, width: Math.round(26 * k), borderRadius: `${13 * k}px ${13 * k}px ${4 * k}px ${4 * k}px` };
    case 'retro':
      return {
        ...base,
        width: Math.round(28 * k),
        borderRadius: 0,
        background: `repeating-linear-gradient(0deg, ${colors.text} 0 ${16 * k}px, transparent ${16 * k}px ${22 * k}px)`,
      };
    case 'neon':
      return {
        ...base,
        width: Math.round(14 * k),
        boxSizing: 'border-box',
        background: 'transparent',
        border: `${Math.max(2, Math.round(4 * k))}px solid ${colors.accent}`,
        boxShadow: `0 0 ${Math.round(18 * k)}px ${colors.accent}, inset 0 0 ${Math.round(8 * k)}px ${colors.accent}`,
      };
    case 'handmade':
      return {
        ...base,
        width: Math.round(16 * k),
        borderRadius: `${8 * k}px ${3 * k}px ${9 * k}px ${2 * k}px`,
        background: colors.text,
        opacity: 0.85,
        ...(index % 2 === 0 ? { transform: 'rotate(3deg)' } : {}),
      };
    default:
      return { ...base, width: Math.round(24 * k), borderRadius: 12 * k };
  }
}

/** The haze of the short videos (design o and q `.vf-haze`): the accent at 30 % in an ellipse. */
export function shortVideoHaze(colors: StoryColours): string {
  return `radial-gradient(ellipse 75% 32% at 50% 36%, ${storyAlpha(colors.accent, 0.3)}, transparent 72%)`;
}

/** One particle of the Particles style: its place in the bars box and its colour slot. */
export interface ArtworkMotionParticle {
  x: number;
  y: number;
  size: number;
  opacity: number;
  colour: 'accent' | 'text';
}

/** The particles of the Particles style (design q: their own layer in place of the bars). */
export const ARTWORK_MOTION_PARTICLE_COUNT = 28;

/**
 * The particles of the Particles style at `t` in a box `w` x `h` px (design q: "Particles keep
 * their own layer in place of the bars"): each one rises through the box a whole number of times
 * over the length, so the last frame equals frame 0; it grows with the level of its band (`levels`
 * per particle, 0..1, already scaled by the envelope). Pure.
 */
export function artworkMotionParticles(
  t: number,
  lengthSec: number,
  box: { w: number; h: number },
  levels: readonly number[]
): ArtworkMotionParticle[] {
  return Array.from({ length: ARTWORK_MOTION_PARTICLE_COUNT }, (_, index) => {
    const seedX = Math.abs((Math.sin(index * 12.9898) * 43758.5453) % 1);
    const seedY = Math.abs((Math.sin(index * 78.233) * 12345.6789) % 1);
    const turns = 1 + (index % 3);
    const rise = (seedY + (turns * t) / lengthSec) % 1;
    const level = clamp01(levels[index % Math.max(1, levels.length)] ?? 0);
    const size = (6 + 18 * level) * (box.h / STACK.barsHeight);
    // Fade in at the foot and out at the top of the box, so a particle never pops.
    const edge = Math.min(1, rise / 0.2, (1 - rise) / 0.2);
    return {
      x: round3(seedX * (box.w - size)),
      y: round3((1 - rise) * (box.h - size)),
      size: round3(size),
      opacity: round3((0.35 + 0.6 * level) * edge),
      colour: index % 4 === 0 ? 'text' : 'accent',
    };
  });
}
